import { useState, useEffect, useRef, useMemo } from 'react'
import { AlertCircle, Redo2, Undo2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { useScenarios } from '@/hooks/useScenarios'
import type { RouteWithAction } from '@/types'
import { DIST_THRESHOLD } from '@/types'
import { MapCanvas, type SelectedPoint } from '@/components/map/MapCanvas'
import type { EditMarkDisplay } from '@/common/drawUtil'
import { planSlotMove, isMovablePointId } from '@/common/slotMoveUtil'
import Button from '@/components/common/Button'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import BrowseSidebar, { type BrowseTab } from './BrowseSidebar'
import BrowseToolbar from './BrowseToolbar'
import ComposerToolbar from './ComposerToolbar'
import ComposerRoutePanel from './ComposerRoutePanel'
import PropertiesPanel from './PropertiesPanel'
import SlotBulkEditPanel from './SlotBulkEditPanel'
import RouteEditSidebar from './RouteEditSidebar'
import RouteEditToolbar from './RouteEditToolbar'
import RouteEditMarkPanel from './RouteEditMarkPanel'
import UsedRoutesPanel from './UsedRoutesPanel'

interface ScenarioWorkspaceProps {
  s: ReturnType<typeof useScenarios>
}

export default function ScenarioWorkspace({ s }: ScenarioWorkspaceProps) {
  const { t } = useTranslation()
  // シナリオ編集（カート編集）モード
  const isComposing = s.viewMode === 'create' || s.viewMode === 'edit'
  // ルート編集（マーク列編集）モード
  const isRouteEditing = s.viewMode === 'routeEdit'
  const isEditing = isComposing

  // ルート編集中は編集対象を除いた全ルートを淡色の背景として表示する
  const routeEditBackgroundRoutes = useMemo(() => {
    if (!isRouteEditing || !s.routeEdit.editingRoute) return s.displayRoutes
    const editingMapId = s.routeEdit.editingRoute.mapId
    return s.displayRoutes.filter((r) => r.mapId !== editingMapId)
  }, [isRouteEditing, s.routeEdit.editingRoute, s.displayRoutes])

  // 編集マークの表示情報（theta は区間方向から算出済み）
  const editMarks = useMemo<EditMarkDisplay[] | null>(() => {
    if (!isRouteEditing) return null
    return s.routeEdit.marks.map((m, i) => ({
      x: m.x,
      y: m.y,
      theta: s.routeEdit.markThetas[i] ?? 0,
      isCargo: m.isCargo,
      reverse: i > 0 && m.reverse,
    }))
  }, [isRouteEditing, s.routeEdit.marks, s.routeEdit.markThetas])

  // エラーのあるマークインデックスの集合
  const markErrorIndices = useMemo(() => {
    const set = new Set<number>()
    s.routeEdit.markErrors.forEach((e) => set.add(e.index))
    return set
  }, [s.routeEdit.markErrors])

  // 描画対象はシナリオ編集中はカート、ルート編集中は背景ルート、参照中は全ルート。
  // 縮尺・中心は常に全ルート（displayRoutes）基準に固定し、編集/選択で縮尺を変えない。
  const mapRoutes = isRouteEditing
    ? routeEditBackgroundRoutes
    : isComposing
      ? s.previewRoutes
      : s.displayRoutes

  const mapRef = useRef<HTMLDivElement>(null)
  // マップ上で選択した点。単一選択はスロット / 中継点、
  // 複数選択（修飾キー・矩形）はスロットのみを保持する。
  const [selectedPoints, setSelectedPoints] = useState<SelectedPoint[]>([])
  const selectedPoint = selectedPoints.length === 1 ? selectedPoints[0] : null
  const selectedPointIds = useMemo(
    () => new Set(selectedPoints.map((p) => p.id)),
    [selectedPoints]
  )

  // --- 選択スロットの一括平行移動 ---
  // ドラッグ中のオフセット。ドラッグ終了 / Δ入力確定で編集セッション
  // （s.pendingRouteMarks）へ積まれて 0 に戻る。機体へは「まとめて保存」まで未送信。
  const [slotMoveOffset, setSlotMoveOffset] = useState({ x: 0, y: 0 })
  // ドラッグ終了時に最新のオフセットを参照するための ref（state 反映待ちを避ける）
  const moveOffsetRef = useRef({ x: 0, y: 0 })
  // ドラッグ開始時点のオフセット（ドラッグ中は開始値 + 差分にする）
  const moveDragBaseRef = useRef({ x: 0, y: 0 })
  const isMovingSlots = s.slotMoveProgress !== null

  // 参照モードで2件以上選択時のみ、記録ルートの端点スロット / 中継点を
  // ドラッグ移動できる
  const movablePointIds = useMemo(() => {
    if (isEditing || isRouteEditing || isMovingSlots) return undefined
    if (selectedPoints.length < 2) return undefined
    const ids = new Set<string>()
    for (const p of selectedPoints) {
      if (isMovablePointId(p.id)) ids.add(p.id)
    }
    return ids.size > 0 ? ids : undefined
  }, [isEditing, isRouteEditing, isMovingSlots, selectedPoints])

  // 選択が変わったら未保存の移動オフセット・高さ変更は破棄する
  const selectionKey = useMemo(
    () =>
      selectedPoints
        .map((p) => p.id)
        .sort()
        .join('|'),
    [selectedPoints]
  )
  useEffect(() => {
    setSlotMoveOffset({ x: 0, y: 0 })
    moveOffsetRef.current = { x: 0, y: 0 }
  }, [selectionKey])

  // 移動プラン（対象ルートのマーク列と検証結果）。パネル表示とステージの両方で使う。
  // 編集セッション反映後のマップ（editedMap）を基準にするため、編集を重ねられる。
  const movePlan = useMemo(() => {
    if (!movablePointIds || !s.editedMap) return null
    return planSlotMove(s.editedMap, [...movablePointIds], slotMoveOffset)
  }, [movablePointIds, s.editedMap, slotMoveOffset])
  // プロパティパネルのフローティング位置（マップ左上基準の px）。null=未配置。
  const [panelPos, setPanelPos] = useState<{ x: number; y: number } | null>(
    null
  )
  // 一括編集パネルのフローティング位置（マップ右側基準）。null=未配置。
  const [bulkPanelPos, setBulkPanelPos] = useState<{
    x: number
    y: number
  } | null>(null)
  // 使用ルート一覧パネルのフローティング位置（マップ右下基準）と開閉状態。null=未配置。
  const [routesPanelPos, setRoutesPanelPos] = useState<{
    x: number
    y: number
  } | null>(null)
  const [routesPanelHeight, setRoutesPanelHeight] = useState(360)
  const [routesPanelOpen, setRoutesPanelOpen] = useState(true)
  // ルート編集モード: 選択マーク編集パネルのフローティング位置（マップ右側基準）。null=未配置。
  const [markPanelPos, setMarkPanelPos] = useState<{
    x: number
    y: number
  } | null>(null)
  // シナリオ編集モード: ルート選択パネルのフローティング位置（マップ左側基準）と高さ。null=未配置。
  const [composerPanelPos, setComposerPanelPos] = useState<{
    x: number
    y: number
  } | null>(null)
  const [composerPanelHeight, setComposerPanelHeight] = useState(440)
  // 参照モードで使用ルート一覧から選択した行（青ハイライト対象）。
  const [browseRouteIndex, setBrowseRouteIndex] = useState<number | null>(null)
  // 参照モードのサイドバータブ。マップ上のツールバー（新規作成 / 編集の対象）と連動する。
  const [browseTab, setBrowseTab] = useState<BrowseTab>('routes')

  // 編集モードはカート、参照モードは選択中シナリオの構成を表示する。
  const routesPanelItems = isEditing ? s.cart : s.selectedScenarioRoutes
  // 参照モードで複数シナリオを選択している場合は一覧を出さない（どのルートか曖昧になるため）。
  const isMultiScenario = !isEditing && s.selectedScenarioIds.size > 1
  const showRoutesPanel =
    routesPanelOpen &&
    routesPanelItems.length > 0 &&
    !isMultiScenario &&
    !isRouteEditing

  // 編集モード: 前のルートと繋がっていない（距離エラー）カート行のインデックス集合。
  const disconnectedIndices = useMemo(() => {
    if (!isEditing) return undefined
    const set = new Set<number>()
    s.cartErrors.forEach((e) => {
      if (e.distanceError !== null) set.add(e.index)
    })
    return set
  }, [isEditing, s.cartErrors])

  // 参照モード: 使用ルート一覧で選択したルートを青でハイライトする displayRoutes 内インデックス。
  const blueHighlightedIndices = useMemo(() => {
    if (isEditing || isRouteEditing || browseRouteIndex === null)
      return undefined
    const mapId = s.selectedScenarioRoutes[browseRouteIndex]?.mapId
    if (!mapId) return undefined
    const set = new Set<number>()
    s.displayRoutes.forEach((r, i) => {
      if (r.mapId === mapId) set.add(i)
    })
    return set
  }, [
    isEditing,
    isRouteEditing,
    browseRouteIndex,
    s.selectedScenarioRoutes,
    s.displayRoutes,
  ])

  // モード切替・サイドバータブ切替時は点選択を解除する
  // （複数選択は参照モードの「ルート」タブでのみ使えるため）
  useEffect(() => {
    setSelectedPoints([])
  }, [s.viewMode, browseTab])

  // モード切替・シナリオ選択の変化でパネルを開き直し、行選択をリセットする
  useEffect(() => {
    setRoutesPanelOpen(true)
    setBrowseRouteIndex(null)
  }, [s.viewMode, s.selectedScenarioIds])

  // 初回表示時、使用ルート一覧パネルを右上のフィットボタン群の下に配置する
  useEffect(() => {
    if (showRoutesPanel && routesPanelPos === null && mapRef.current) {
      const r = mapRef.current.getBoundingClientRect()
      const PANEL_W = 320 // w-80
      const CONTROLS_RIGHT = 8 // MapCanvasControls の right-2
      const CONTROLS_BOTTOM = 184 // top-2 + ズーム/フィットボタン群(最大4個)の高さ目安
      setRoutesPanelPos({
        x: Math.max(12, r.width - PANEL_W - CONTROLS_RIGHT),
        y: CONTROLS_BOTTOM,
      })
    }
  }, [showRoutesPanel, routesPanelPos])

  // シナリオ編集モードに入ったとき、ルート選択パネルを左側
  // （左上のツールボタン群の下）に配置する
  useEffect(() => {
    if (isComposing && composerPanelPos === null && mapRef.current) {
      const TOOLS_LEFT = 8 // 左上ツールボタン群の left-2
      const TOOLS_BOTTOM = 110 // top-2 + ボタン2個分の高さ目安
      setComposerPanelPos({ x: TOOLS_LEFT, y: TOOLS_BOTTOM })
    }
  }, [isComposing, composerPanelPos])

  // ルート編集モードで初めてマークを選択したとき、編集パネルを
  // 右上のズーム/フィットボタン群の下（マップ右側）に配置する
  const hasSelectedMark =
    isRouteEditing && s.routeEdit.selectedMarkIndex !== null
  useEffect(() => {
    if (hasSelectedMark && markPanelPos === null && mapRef.current) {
      const r = mapRef.current.getBoundingClientRect()
      const PANEL_W = 256 // w-64
      const CONTROLS_RIGHT = 8 // MapCanvasControls の right-2
      const CONTROLS_BOTTOM = 184 // top-2 + ズーム/フィットボタン群(最大4個)の高さ目安
      setMarkPanelPos({
        x: Math.max(12, r.width - PANEL_W - CONTROLS_RIGHT),
        y: CONTROLS_BOTTOM,
      })
    }
  }, [hasSelectedMark, markPanelPos])

  // 初回選択時、プロパティパネル（単一選択）を左下に配置する
  useEffect(() => {
    if (selectedPoints.length > 0 && panelPos === null && mapRef.current) {
      const r = mapRef.current.getBoundingClientRect()
      const PANEL_H = 200
      setPanelPos({
        x: 12,
        y: Math.max(12, r.height - PANEL_H - 12),
      })
    }
  }, [selectedPoints, panelPos])

  // 初回の複数選択時、一括編集パネルをマップ右側
  // （右上のズーム/フィットボタン群の下）に配置する
  useEffect(() => {
    if (selectedPoints.length >= 2 && bulkPanelPos === null && mapRef.current) {
      const r = mapRef.current.getBoundingClientRect()
      const PANEL_W = 288 // w-72
      const CONTROLS_RIGHT = 8 // MapCanvasControls の right-2
      const CONTROLS_BOTTOM = 184 // top-2 + ズーム/フィットボタン群(最大4個)の高さ目安
      setBulkPanelPos({
        x: Math.max(12, r.width - PANEL_W - CONTROLS_RIGHT),
        y: CONTROLS_BOTTOM,
      })
    }
  }, [selectedPoints, bulkPanelPos])

  // マップ上の点クリック。修飾キーなしは単一選択（置き換え）、
  // Ctrl・Cmd はトグル、Shift は追加（スロット / 中継点とも対象）。
  const handleSelectPoint = (
    p: SelectedPoint | null,
    mods: { ctrl: boolean; shift: boolean }
  ) => {
    if (!p) {
      if (!mods.ctrl && !mods.shift) setSelectedPoints([])
      return
    }
    if (mods.ctrl || mods.shift) {
      setSelectedPoints((prev) => {
        const exists = prev.some((q) => q.id === p.id)
        if (mods.ctrl && exists) return prev.filter((q) => q.id !== p.id)
        return exists ? prev : [...prev, p]
      })
    } else {
      setSelectedPoints([p])
    }
  }

  // 矩形選択・重なりグループのクリックで複数の点（スロット / 中継点）を選択。
  // 修飾キーで追加、なしは置き換え。Ctrl で全員選択済みのグループは選択解除。
  const handleSelectSlots = (
    slots: SelectedPoint[],
    mods: { ctrl: boolean; shift: boolean }
  ) => {
    setSelectedPoints((prev) => {
      if (
        mods.ctrl &&
        slots.length > 0 &&
        slots.every((p) => prev.some((q) => q.id === p.id))
      ) {
        const ids = new Set(slots.map((p) => p.id))
        return prev.filter((q) => !ids.has(q.id))
      }
      const base = mods.ctrl || mods.shift ? [...prev] : []
      const seen = new Set(base.map((q) => q.id))
      for (const p of slots) {
        if (seen.has(p.id)) continue
        seen.add(p.id)
        base.push(p)
      }
      return base
    })
  }

  // マップ上のルートクリック（修飾キーなしのみ）。空白クリックは全解除。
  const handleSelectRoute = (
    route: RouteWithAction | null,
    mods: { ctrl: boolean; shift: boolean }
  ) => {
    if (isEditing || isRouteEditing) return
    if (route) {
      s.selectRouteWithMods(route.id, mods)
    } else if (!mods.ctrl && !mods.shift) {
      s.handleClearBrowseSelection()
    }
  }

  // 高さ変更を編集セッションへ積む（機体へは「まとめて保存」まで未送信）
  const handleStageHeights = (heightMm: number) => {
    const ids = selectedPoints
      .filter((p) => p.kind === 'slot' && p.isCargo)
      .map((p) => p.id)
    if (ids.length === 0) return
    s.stageSlotHeights(ids, heightMm)
  }

  // 参照モードの回転対象となる端点スロット ID 群。単独選択に加え、
  // 同一座標・同一向きで重なった端点スロットのみの複数選択（接続された
  // ルート同士が共有するスロット）もまとめて回転できる。
  const rotatableSlotIds = useMemo(() => {
    if (isEditing || isRouteEditing || selectedPoints.length === 0) return null
    const first = selectedPoints[0]
    const sameGroup = selectedPoints.every((p) => {
      if (p.kind !== 'slot' || !/^\*r[^/]+\/rec-[12]$/.test(p.id)) return false
      const dTheta = Math.abs(
        Math.atan2(
          Math.sin(p.theta - first.theta),
          Math.cos(p.theta - first.theta)
        )
      )
      return (
        Math.abs(p.x - first.x) + Math.abs(p.y - first.y) <= DIST_THRESHOLD &&
        dTheta <= 0.001
      )
    })
    return sameGroup ? selectedPoints.map((p) => p.id) : null
  }, [isEditing, isRouteEditing, selectedPoints])
  // 回転ハンドルの表示位置（グループは同一座標なので先頭の点に描く）
  const browseRotatablePointId = rotatableSlotIds
    ? (selectedPoints[0]?.id ?? null)
    : null

  // 端点スロットの向き変更を編集セッションへ積む（重なりグループは全件へ適用）
  const handleRotatePoint = (thetaRad: number) => {
    if (!rotatableSlotIds) return
    const rotated = new Set<string>()
    for (const id of rotatableSlotIds) {
      if (s.stageEndpointRotation(id, thetaRad)) rotated.add(id)
    }
    if (rotated.size === 0) return
    setSelectedPoints((prev) =>
      prev.map((p) => (rotated.has(p.id) ? { ...p, theta: thetaRad } : p))
    )
  }

  // 編集セッションの保存 / 破棄の確認ダイアログ
  const [showSaveEditsConfirm, setShowSaveEditsConfirm] = useState(false)
  const [showDiscardEditsConfirm, setShowDiscardEditsConfirm] = useState(false)
  const isSavingEdits = s.isSavingSlotHeights || isMovingSlots

  // --- 選択スロットのドラッグ平行移動 ---
  const handleSelectionDragStart = () => {
    moveDragBaseRef.current = slotMoveOffset
  }
  const handleSelectionDrag = (delta: { x: number; y: number }) => {
    const next = {
      x: moveDragBaseRef.current.x + delta.x,
      y: moveDragBaseRef.current.y + delta.y,
    }
    moveOffsetRef.current = next
    setSlotMoveOffset(next)
  }

  // 移動オフセットを編集セッションへ積む（ドラッグ終了 / Δ入力確定時）。
  // プランが不正な場合はオフセット表示を残し、パネルのエラー表示に委ねる
  // （その状態ではセッションに積まれず保存もされない）。
  const stageMoveOffset = (offset: { x: number; y: number }) => {
    if (
      !movablePointIds ||
      !s.editedMap ||
      (offset.x === 0 && offset.y === 0)
    ) {
      moveOffsetRef.current = { x: 0, y: 0 }
      setSlotMoveOffset({ x: 0, y: 0 })
      return
    }
    const plan = planSlotMove(s.editedMap, [...movablePointIds], offset)
    if (!plan.ok) {
      moveOffsetRef.current = offset
      setSlotMoveOffset(offset)
      return
    }
    s.stageRouteMarks(plan.jobs)
    // 選択点の表示座標も移動後へ更新する（選択は維持）
    const ids = movablePointIds
    setSelectedPoints((prev) =>
      prev.map((p) =>
        ids.has(p.id) ? { ...p, x: p.x + offset.x, y: p.y + offset.y } : p
      )
    )
    moveOffsetRef.current = { x: 0, y: 0 }
    setSlotMoveOffset({ x: 0, y: 0 })
  }
  const handleSelectionDragEnd = () => stageMoveOffset(moveOffsetRef.current)

  const hasErrors = isEditing && s.cartErrors.length > 0

  return (
    <div className="flex-1 flex min-h-0">
      {/* 左: マップ（常時表示） */}
      <div ref={mapRef} className="flex-1 relative min-w-0 bg-gray-100">
        {/* ルートが無くても背景画像があればマップを表示する */}
        {s.displayRoutes.length === 0 && !isRouteEditing && !s.imageLoaded ? (
          <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm">
            {t('scenarios.noRoutes')}
          </div>
        ) : (
          <MapCanvas
            // 編集セッション反映後のマップ（編集が無ければ元マップと同一）
            map={s.editedMap}
            canvasParams={s.canvasParams}
            bgImage={s.bgImage}
            imageLoaded={s.imageLoaded}
            routes={mapRoutes}
            fitRoutes={s.displayRoutes}
            // フィット基準は編集前のマップに固定し、スロット編集でズームが戻らないようにする
            fitMap={s.map}
            autoFit={true}
            showControls={true}
            // ルート編集中も既存スロットを表示する（マーク追加時の吸着先）。
            // ラベルは編集の妨げになるため編集中は消す。
            showSlots={true}
            showSlotLabels={!isRouteEditing}
            highlightLastRoute={
              isEditing &&
              (!!s.previewRoute || s.previewWait || s.previewWaitResume)
            }
            selectedRouteIndex={isEditing ? s.selectedRouteIndex : undefined}
            forkAnimFirstRouteIndex={
              isEditing
                ? s.previewRoute
                  ? s.previewRoutes.length - 1
                  : s.selectedRouteIndex
                : null
            }
            // 参照モードは選択シナリオの構成ルートをシナリオ順に走行させる。
            // 未選択時は空配列になり再生ボタンごと非表示になる。
            forkAnimRoutes={
              isEditing || isRouteEditing ? undefined : s.selectedScenarioRoutes
            }
            // 参照モードは停止状態で開始し、再生ボタンで走行を始める
            forkAnimDefaultEnabled={isEditing}
            highlightedIndices={
              isEditing || isRouteEditing
                ? undefined
                : s.highlightedBrowseIndices
            }
            blueHighlightedIndices={
              isEditing || isRouteEditing ? undefined : blueHighlightedIndices
            }
            selectedPointIds={isRouteEditing ? null : selectedPointIds}
            onSelectPoint={isRouteEditing ? undefined : handleSelectPoint}
            onSelectRoute={isRouteEditing ? undefined : handleSelectRoute}
            // 複数選択（矩形・修飾キー）は参照モードの「ルート」タブでのみ有効
            onSelectSlots={
              !isEditing && !isRouteEditing && browseTab === 'routes'
                ? handleSelectSlots
                : undefined
            }
            movablePointIds={movablePointIds}
            rotatablePointId={browseRotatablePointId}
            onPointRotate={handleRotatePoint}
            // 回転ハンドルのドラッグは Undo 履歴上で1操作にまとめる
            onPointRotateStart={s.beginMapEditBatch}
            onPointRotateEnd={s.endMapEditBatch}
            selectionOffset={movablePointIds ? slotMoveOffset : null}
            onSelectionDragStart={handleSelectionDragStart}
            onSelectionDrag={movablePointIds ? handleSelectionDrag : undefined}
            onSelectionDragEnd={handleSelectionDragEnd}
            dimRoutes={isRouteEditing}
            editMarks={editMarks}
            selectedMarkIndex={s.routeEdit.selectedMarkIndex}
            markErrorIndices={markErrorIndices}
            isAddingMark={isRouteEditing && s.routeEdit.isAddingMark}
            rotatableMarkIndex={s.routeEdit.rotatableMarkIndex}
            onSelectMark={s.routeEdit.selectMark}
            onMarkDragStart={s.routeEdit.handleMarkDragStart}
            onMarkDrag={s.routeEdit.handleMarkDrag}
            onMarkDragEnd={s.routeEdit.handleMarkDragEnd}
            onMarkRotateStart={s.routeEdit.handleMarkRotateStart}
            onMarkRotate={s.routeEdit.handleMarkRotate}
            onMarkRotateEnd={s.routeEdit.handleMarkRotateEnd}
            onAddMarkAt={s.routeEdit.addMarkAt}
          />
        )}

        {/* 背景画像のみ表示中（ルート0件）でも状況が分かるようにメッセージを重ねる */}
        {s.displayRoutes.length === 0 && !isRouteEditing && s.imageLoaded && (
          <div className="absolute top-2 left-1/2 -translate-x-1/2 z-10 px-3 py-1 rounded bg-white/80 text-gray-500 text-sm shadow pointer-events-none">
            {t('scenarios.noRoutes')}
          </div>
        )}

        {/* 参照モード: シナリオ / ルートの新規作成・編集のフローティングツールバー */}
        {!isEditing && !isRouteEditing && (
          <BrowseToolbar s={s} tab={browseTab} />
        )}

        {/* シナリオ編集モード: シナリオ名 / キャンセル / 登録のツールバーとルート選択パネル */}
        {isEditing && <ComposerToolbar s={s} />}
        {isEditing && composerPanelPos && (
          <ComposerRoutePanel
            s={s}
            pos={composerPanelPos}
            onMove={setComposerPanelPos}
            height={composerPanelHeight}
            onResize={setComposerPanelHeight}
          />
        )}

        {/* ルート編集モード: マーク追加 / Undo / Redo / キャンセル / 保存のフローティングツールバー */}
        {isRouteEditing && <RouteEditToolbar s={s} />}

        {/* ルート編集モード: 選択マークの編集（フローティングパネル） */}
        {isRouteEditing && markPanelPos && (
          <RouteEditMarkPanel
            s={s}
            pos={markPanelPos}
            onMove={setMarkPanelPos}
          />
        )}

        {/* 編集モード: ルート間エラーのサマリ */}
        {hasErrors && (
          <div className="absolute bottom-0 left-0 right-0 bg-white/95 border-t p-2 max-h-24 overflow-y-auto">
            {s.cartErrors.map((error) => (
              <div key={`error-${error.index}`}>
                {error.distanceError && (
                  <div className="text-xs text-red-600 flex items-center gap-1 mb-1">
                    <AlertCircle size={12} />
                    {t('scenarios.routeDistanceError', {
                      index: error.index + 1,
                      distance: error.distanceError,
                    })}
                  </div>
                )}
                {s.enableCargoToggle && error.cargoError && (
                  <div className="text-xs text-red-600 flex items-center gap-1 mb-1">
                    <AlertCircle size={12} />
                    {t('scenarios.routeCargoError', { index: error.index + 1 })}
                  </div>
                )}
                {error.sequenceError && (
                  <div className="text-xs text-red-600 flex items-center gap-1 mb-1">
                    <AlertCircle size={12} />
                    {t(
                      error.sequenceError === 'moveAfterLoaded'
                        ? 'scenarios.routeSequenceMoveError'
                        : 'scenarios.routeSequenceLoadedError',
                      { index: error.index + 1 }
                    )}
                  </div>
                )}
                {error.cargoStateError && (
                  <div className="text-xs text-red-600 flex items-center gap-1 mb-1">
                    <AlertCircle size={12} />
                    {t(
                      error.cargoStateError === 'pickupWhileLoaded'
                        ? 'scenarios.routePickupWhileLoadedError'
                        : error.cargoStateError === 'placeWithoutLoad'
                          ? 'scenarios.routePlaceWithoutLoadError'
                          : 'scenarios.routeLoadedAfterPlaceError',
                      { index: error.index + 1 }
                    )}
                  </div>
                )}
                {error.waitError && (
                  <div className="text-xs text-red-600 flex items-center gap-1 mb-1">
                    <AlertCircle size={12} />
                    {t('scenarios.routeWaitSecError', {
                      index: error.index + 1,
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* 選択した点のプロパティ（フローティングパネル）。
            参照モードの記録ルート端点スロットは向きを編集できる（セッションへ積む） */}
        {selectedPoint && panelPos && (
          <PropertiesPanel
            point={selectedPoint}
            pos={panelPos}
            onMove={setPanelPos}
            onClose={() => setSelectedPoints([])}
            onRotate={browseRotatablePointId ? handleRotatePoint : undefined}
          />
        )}

        {/* 複数スロット選択時の一括編集（フローティングパネル） */}
        {selectedPoints.length >= 2 && bulkPanelPos && (
          <SlotBulkEditPanel
            points={selectedPoints}
            pos={bulkPanelPos}
            onMove={setBulkPanelPos}
            onClose={() => setSelectedPoints([])}
            isSaving={isSavingEdits}
            pendingHeights={s.pendingHeights}
            onStageHeight={handleStageHeights}
            moveOffset={movablePointIds ? slotMoveOffset : null}
            movePlan={movePlan}
            onMoveOffsetChange={stageMoveOffset}
          />
        )}

        {/* 編集セッションの未保存編集バー（Undo / Redo / まとめて保存 / 破棄）。
            全て Undo した直後も Redo できるよう、履歴が残っている間は表示する */}
        {!isEditing &&
          !isRouteEditing &&
          (s.hasPendingMapEdits || s.canUndoMapEdit || s.canRedoMapEdit) && (
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 bg-white/95 rounded-lg shadow px-3 py-2">
              <span className="text-xs text-gray-700">
                {t('scenarios.pendingEditsSummary', {
                  routes: s.pendingEditCounts.routes,
                  heights: s.pendingEditCounts.heights,
                })}
              </span>
              <Button
                variant="white"
                size="sm"
                disabled={isSavingEdits || !s.canUndoMapEdit}
                onClick={() => {
                  s.undoMapEdit()
                  // 選択点の表示座標が変わるため選択は解除する
                  setSelectedPoints([])
                }}
                className="p-1.5"
                title={t('scenarios.undo')}
              >
                <Undo2 size={14} />
              </Button>
              <Button
                variant="white"
                size="sm"
                disabled={isSavingEdits || !s.canRedoMapEdit}
                onClick={() => {
                  s.redoMapEdit()
                  setSelectedPoints([])
                }}
                className="p-1.5"
                title={t('scenarios.redo')}
              >
                <Redo2 size={14} />
              </Button>
              <Button
                variant="white"
                size="sm"
                disabled={isSavingEdits}
                onClick={() => {
                  // 未保存の編集が無い（全て Undo 済みで履歴だけ残っている）
                  // 場合は失うものが無いため、確認なしで履歴ごとクリアする
                  if (s.hasPendingMapEdits) {
                    setShowDiscardEditsConfirm(true)
                  } else {
                    s.discardPendingMapEdits()
                    setSelectedPoints([])
                  }
                }}
              >
                {t('scenarios.discard')}
              </Button>
              <Button
                variant="blue"
                size="sm"
                disabled={isSavingEdits || !s.hasPendingMapEdits}
                onClick={() => setShowSaveEditsConfirm(true)}
              >
                {isSavingEdits
                  ? t('scenarios.saving')
                  : t('scenarios.bulkSave')}
              </Button>
            </div>
          )}

        {/* 編集セッションの保存 / 破棄の確認 */}
        <ConfirmDialog
          isOpen={showSaveEditsConfirm}
          title={t('scenarios.bulkSaveConfirmTitle')}
          message={t('scenarios.pendingSaveConfirmMessage', {
            routes: s.pendingEditCounts.routes,
            heights: s.pendingEditCounts.heights,
          })}
          confirmLabel={t('scenarios.bulkSave')}
          cancelLabel={t('scenarios.cancel')}
          onConfirm={async () => {
            setShowSaveEditsConfirm(false)
            const ok = await s.applyPendingMapEdits()
            // 再記録でルート ID（スロット ID）が変わるため選択は解除する
            if (ok) setSelectedPoints([])
          }}
          onCancel={() => setShowSaveEditsConfirm(false)}
        />
        <ConfirmDialog
          isOpen={showDiscardEditsConfirm}
          title={t('scenarios.pendingDiscardConfirmTitle')}
          message={t('scenarios.pendingDiscardConfirmMessage')}
          confirmLabel={t('scenarios.discard')}
          isAlert={true}
          onConfirm={() => {
            setShowDiscardEditsConfirm(false)
            s.discardPendingMapEdits()
            // 選択点の表示座標が編集前に戻るため選択も解除する
            setSelectedPoints([])
          }}
          onCancel={() => setShowDiscardEditsConfirm(false)}
        />

        {/* スロット一括移動の保存中オーバーレイ（再記録は時間がかかるため操作をブロック） */}
        {s.slotMoveProgress && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center">
            <div className="bg-white rounded-lg px-6 py-4 shadow-xl text-sm text-gray-800">
              {t('scenarios.bulkMoveProgress', {
                done: s.slotMoveProgress.done,
                total: s.slotMoveProgress.total,
              })}
            </div>
          </div>
        )}

        {/* 使用ルート一覧（フローティングパネル） */}
        {showRoutesPanel && routesPanelPos && (
          <UsedRoutesPanel
            routes={routesPanelItems}
            thumbnails={s.thumbnails}
            pos={routesPanelPos}
            onMove={setRoutesPanelPos}
            onClose={isEditing ? undefined : () => setRoutesPanelOpen(false)}
            height={routesPanelHeight}
            onResize={setRoutesPanelHeight}
            selectedIndex={isEditing ? s.selectedRouteIndex : browseRouteIndex}
            selectedColor={isEditing ? 'orange' : 'blue'}
            errorIndices={disconnectedIndices}
            onSelect={
              isEditing
                ? s.handleToggleSelectedRoute
                : (index) =>
                    setBrowseRouteIndex((prev) =>
                      prev === index ? null : index
                    )
            }
            onDelete={isEditing ? s.handleRequestDelete : undefined}
            onWaitChange={isEditing ? s.handleCartWaitSecChange : undefined}
          />
        )}
      </div>

      {/* 右: モード別サイドバー。シナリオ編集モードはフローティング UI のみで完結するため非表示 */}
      {!isEditing && (
        <div className="w-[28rem] flex-shrink-0 border-l border-gray-200 flex flex-col min-h-0 bg-white">
          {isRouteEditing ? (
            <RouteEditSidebar s={s} />
          ) : (
            <BrowseSidebar s={s} tab={browseTab} onTabChange={setBrowseTab} />
          )}
        </div>
      )}
    </div>
  )
}
